import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import orderServices from "../../services/orders/orderservice";
import OrdersLayer from "./OrdersLayer";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import DriverSummaryPanel from "./DriverSummaryPanel";
import OrdersDialog from "./OrdersDialog";
import { Calendar } from "primereact/calendar";
import { Tooltip } from "primereact/tooltip";

export type Order = {
  orderID: number;
  gpsLocation: string;
  sequenceNo: number;
  color?: string | null;
  driverName?: string | null;
  modifiedDate?: string | null;
  taxableAmount?: number | null;
  userName?: string | null;
  userNumber?: string | null;
  isInvoiced?: boolean | null;
  delayedStatusId?: number | null;
  delayedStatusName?: string | null;
  driverNumber?: string | null;
  totalOrderDelivered?: number;
  totalReturnOrCancelled?: number;
  totalAssignedTodayForDriver?: number;
  totalInvoicedForDriver?: number;
  totalCancelledForDriver?: number;
  driverID?: number | null;
  isCancelled?: number | boolean | null;
};

function parseGpsLocation(gps?: string | null): { lat: number; lng: number } | null {
  if (!gps || !gps.includes(",")) return null;
  const [a, b] = gps.split(",");
  const lat = parseFloat(a.trim());
  const lng = parseFloat(b.trim());
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

function gpsKey(gps?: string | null): string | null {
  if (!gps) return null;
  return gps.replace(/\s+/g, "").trim();
}

function deduplicateByLocation(list: Order[]): Order[] {
  const byLoc = new Map<string, Order>();
  for (const o of list) {
    const key = gpsKey(o.gpsLocation);
    if (!key) continue;
    const prev = byLoc.get(key);
    if (!prev || (o.sequenceNo ?? 0) > (prev.sequenceNo ?? 0)) {
      byLoc.set(key, o);
    }
  }
  return Array.from(byLoc.values());
}

function groupByLocation(list: Order[]): Record<string, Order[]> {
  const g: Record<string, Order[]> = {};
  for (const o of list) {
    const key = gpsKey(o.gpsLocation);
    if (!key) continue;
    (g[key] ??= []).push(o);
  }
  Object.values(g).forEach(arr => arr.sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0)));
  return g;
}
function groupByDriver(orders: Order[]) {
  const map: Record<
    number,
    {
      driverID: number;
      driverName: string;
      driverNumber: string;
      assigned: number;
      delivered: number;
      cancelled: number;
      pending: number;
      totaldelayed: number;
      color: string;
    }
  > = {};

  for (const o of orders) {
    if (!o.driverID) continue;

    if (!map[o.driverID]) {
      map[o.driverID] = {
        driverID: o.driverID,
        driverName: o.driverName ?? "—",
        driverNumber: o.driverNumber ?? "—",
        assigned: 0,
        delivered: 0,
        cancelled: 0,
        pending: 0,
        totaldelayed: 0,
        color: o.color ?? "#3b82f6",
      };
    }

    const d = map[o.driverID];

    d.assigned++;

    if (o.isInvoiced) {
     
      d.delivered++;
    } else if (o.totalReturnOrCancelled && o.totalReturnOrCancelled > 0) {
     
      d.cancelled += o.totalReturnOrCancelled;
    } else if (o.delayedStatusId) {
      
      d.totaldelayed++;
    } else {
     
      d.pending++;
    }
  }

  return Object.values(map);
}
 
export default function OrdersMap() {
  const [rawOrders, setRawOrders] = useState<Order[]>([]);
  const [totals, setTotals] = useState({
    totalOrders: 0,
    totalDelivered: 0,
    totalCancelled: 0,
    totalPending: 0,
    totaldelayed: 0,
    totalCancelled1: 0,
  });
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [selectedLocKey, setSelectedLocKey] = useState<string | null>(null);
  const [highlightLocKeys, setHighlightLocKeys] = useState<string[]>([]);

  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [cancelledOrders, setCancelledOrders] = useState<Order[]>([]);
  const [showCancelledDialog, setShowCancelledDialog] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [mapCenter, setMapCenter] = useState({ lat: 12.3051, lng: 76.6552 });
  const [searchId, setSearchId] = useState<number | "">("");
  const mapRef = useRef<google.maps.Map | null>(null);
  const [searchNumber, setSearchNumber] = useState<string>("");
  const [dialogTitle, setDialogTitle] = useState("Orders");
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyAoYLkna0JudEVB9imblFTHGJwGLYyw0_k",
  });

  const deliveredIcon = "/icons/delivered.svg";
const pendingIcon = "/icons/pending.svg";
const delayedIcon = "/icons/delayed.svg";

  const fetchOrders = async (date?: string) => {
  try {
    const data: any = await orderServices.getDriverOrdersDailySummary(date);

    const rawList: any[] = Array.isArray(data?.orders)
      ? data.orders
      : Array.isArray(data)
      ? data
      : [];

    const list: Order[] = Array.from(
      new Map(rawList.map((o: any) => [o.orderID, o])).values()
    );

    setRawOrders(list);
    const totalOrders = list.length;
    const totalDelivered = list.filter(o => o.isInvoiced).length;
    const totalCancelled = list.reduce(
      (acc, o) => acc + (o.totalReturnOrCancelled ?? 0),
      0
    );
    const totaldelayed = list.filter(
      o => !o.isInvoiced && o.delayedStatusId != null
    ).length;
    const totalCancelled1 = totalCancelled + totaldelayed;
    const totalPending = totalOrders - totalDelivered - totalCancelled - totaldelayed;

    setTotals({
      totalOrders,
      totalDelivered,
      totalCancelled,
      totalPending,
      totaldelayed,
      totalCancelled1,
    });
  } catch (err) {
    console.error("Failed to fetch orders", err);
  }
};

  useEffect(() => {
    fetchOrders();
  }, []);

  const ICON_SIZE = 55;
  const pinSvgUrl = (color: string, text: number | string) => {
    const fontSize = Math.floor(ICON_SIZE / 7);
    const svg = `
      <svg width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2c-3.9 0-7 3.1-7 7 0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7z"
          fill="${color}" stroke="white" stroke-width="1"/>
        <text x="12" y="13" text-anchor="middle" font-size="${fontSize}" fill="black" font-weight="bold" font-family="sans-serif">${text}</text>
      </svg>`;
    return "data:image/svg+xml;base64," + btoa(svg);
  };

  const handleSearchByNumber = () => {
  if (!searchNumber) return;

  const matchedOrders = rawOrders.filter(o => o.userNumber === searchNumber);
  if (matchedOrders.length === 0) {
    return alert("No orders found for this customer number!");
  }

  const keys: string[] = [];
  matchedOrders.forEach(order => {
    const key = gpsKey(order.gpsLocation);
    if (key) keys.push(key);
    const pos = parseGpsLocation(order.gpsLocation);
    if (pos) {
      mapRef.current?.panTo(pos); 
      mapRef.current?.setZoom(16);
    }
  });

  setSelectedOrderId(matchedOrders[0].orderID); 
  setSelectedLocKey(keys[0] ?? null);          
  setHighlightLocKeys(keys);
  // Animate for 3 seconds, then clear highlights
  window.setTimeout(() => setHighlightLocKeys([]), 3000);
};


  const formatDate = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`; // yyyy-MM-dd in local timezone
};

  const handleSearchByDate = async () => {
    const dateStr = selectedDate ? formatDate(selectedDate) : undefined;
    await fetchOrders(dateStr);
  };

const uniqueOrders = useMemo(
  () => Array.from(new Map(rawOrders.map(o => [o.orderID, o])).values()),
  [rawOrders]
);

const driversSummary = useMemo(() => groupByDriver(uniqueOrders), [uniqueOrders]);
const groups = useMemo(() => groupByLocation(uniqueOrders), [uniqueOrders]);
const markers = useMemo(() => deduplicateByLocation(uniqueOrders), [uniqueOrders]);

  const handleTagClick = (status: "total" | "delivered" | "pending" | "cancelled") => {
    let filtered: Order[] = [];
    if (status === "total") {
      filtered = rawOrders;
      setDialogTitle("All Orders");
    } else if (status === "delivered") {
      filtered = rawOrders.filter(o => o.isInvoiced);
      setDialogTitle("Delivered Orders");
    } else if (status === "pending") {
      filtered = rawOrders.filter(o => !o.isInvoiced && !o.isCancelled && !o.delayedStatusId);
      setDialogTitle("Yet To Be Delivered Orders");
    } else if (status === "cancelled") {
      filtered = rawOrders.filter(o => o.totalReturnOrCancelled || (o.delayedStatusId && !o.isInvoiced));
      setDialogTitle("Cancelled / Delayed Orders");
    }
    setCancelledOrders(filtered);
    setShowCancelledDialog(true);
  };

  const handleDriverOrdersClick = (driverID: number, status: "assigned" | "delivered" | "pending" | "cancelled" | "total") => {
    setSelectedDriverId(driverID);
    if (status === "total") return;
    let filtered = rawOrders.filter(o => o.driverID === driverID);
    if (status === "delivered") {
      filtered = filtered.filter(o => o.isInvoiced);
      setDialogTitle("Delivered Orders");
    } else if (status === "pending") {
      filtered = filtered.filter(o => !o.isInvoiced && !o.isCancelled && !o.delayedStatusId);
      setDialogTitle("Yet To Deliver Orders");
    } else if (status === "cancelled") {
      filtered = filtered.filter(o => o.totalReturnOrCancelled || (o.delayedStatusId && !o.isInvoiced));
      setDialogTitle("Cancelled / Delayed Orders");
    }
    setCancelledOrders(filtered);
    setShowCancelledDialog(true);
    setGlobalFilter("");
  };

  const handleSearchById = () => {
  if (!searchId) return;

  const matchedOrder = rawOrders.find(o => o.orderID === Number(searchId));
  if (!matchedOrder) {
    return alert("Order not found!");
  }

  const pos = parseGpsLocation(matchedOrder.gpsLocation);
  const key = gpsKey(matchedOrder.gpsLocation);
  if (!pos || !key) return alert("Invalid GPS location!");

  setSelectedOrderId(matchedOrder.orderID);
  setSelectedLocKey(key);
  setMapCenter(pos);
  mapRef.current?.panTo(pos);
  mapRef.current?.setZoom(16);

  setHighlightLocKeys([key]);
  window.setTimeout(() => setHighlightLocKeys([]), 3000);
};


  if (!isLoaded) return <div>Loading map…</div>;

  return (
    <div>
      {/* Search Section */}
      <div style={{ margin: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <InputText
  type="number"
  value={searchId !== "" ? searchId.toString() : ""}
  onChange={e => {
    const val = e.target.value ? Number(e.target.value) : "";
    setSearchId(val);
     if (!val && !searchNumber) {
      setSelectedLocKey(null);
      setSelectedOrderId(null);
    }
  }}
  placeholder="Search Order ID"
/>

<InputText
  type="text"
  value={searchNumber}
  maxLength={12}
  onChange={e => {
    const val = e.target.value;
    if (/^[0-9]*$/.test(val)) {
      setSearchNumber(val);
    }

    if (!val && !searchId) {
      setSelectedLocKey(null);
      setSelectedOrderId(null);
    }
  }}
  placeholder="Search Customer Number"
/>


        <Button
          icon="pi pi-search"
          tooltip="Search"
          tooltipOptions={{ position: "top" }}
          onClick={() => {
            if (searchId && searchNumber) {
              const matchedOrder = rawOrders.find(
                o => o.orderID === Number(searchId) && o.userNumber === searchNumber
              );
              if (matchedOrder) {
                const pos = parseGpsLocation(matchedOrder.gpsLocation);
                const key = gpsKey(matchedOrder.gpsLocation);
                if (!pos || !key) return alert("Invalid GPS location!");
                setSelectedLocKey(key);
                setSelectedOrderId(matchedOrder.orderID);
                setMapCenter(pos);
                mapRef.current?.panTo(pos);
                mapRef.current?.setZoom(16);
                setHighlightLocKeys([key]);
                window.setTimeout(() => setHighlightLocKeys([]), 3000);
              } else {
                alert("Order Is Not Found!");
              }
            } else if (searchId) {
              handleSearchById();
            } else if (searchNumber) {
              handleSearchByNumber();
            } else {
              alert("Please enter Order ID or Customer Number to search!");
            }
          }}
        />

               <Calendar
  value={selectedDate}
  onChange={(e) => setSelectedDate(e.value as Date)}
  dateFormat="dd/mm/yy"       
  maxDate={new Date()}         
  showIcon
  placeholder="Select Date"
   readOnlyInput
/>
        <Button
          //label="Search by Date"
          tooltip="Search by Date"
          tooltipOptions={{ position: "top" }}
          icon="pi pi-search"
          onClick={handleSearchByDate}
        />
                <Button
  icon="pi pi-refresh"
  tooltip="Reset All"
  tooltipOptions={{ position: "top" }}
  // onClick={() => {
  //   setSelectedDriverId(null);
  //   setSearchId("");
  //   setSearchNumber("");
  //   setSelectedLocKey(null);
  //   setSelectedOrderId(null);
  //   setHighlightLocKey(null);
  //   window

  // }}
  onClick={() => window.location.reload()}
/>
        <div style={{ display: "flex", alignItems: "center", marginLeft: 20 }}>

<Button
  label={String(totals.totalOrders)}
  tooltip="Total Orders"
  tooltipOptions={{ position: "top" }}
  onClick={() => handleTagClick("total")}
  style={{ backgroundColor: "#6b7280" }}
/>

  <img
    src={pinSvgUrl("#22c55e", totals.totalDelivered)}
    alt="Delivered"
    data-pr-tooltip={`Delivered: ${totals.totalDelivered}`}
    style={{ cursor: "pointer" }}
    onClick={() => handleTagClick("delivered")}
  />

  <img
    src={pinSvgUrl("#3b82f6", totals.totalPending)}
    alt="Yet to Deliver"
    data-pr-tooltip={`Pending: ${totals.totalPending}`}
    style={{ cursor: "pointer" }}
    onClick={() => handleTagClick("pending")}
  />

  <img
    src={pinSvgUrl("#ef4444", totals.totalCancelled1)}
    alt="Cancelled / Delayed"
    data-pr-tooltip={`Delayed Orders: ${totals.totalCancelled1}`}
    style={{ cursor: "pointer" }}
    onClick={() => handleTagClick("cancelled")}
  />

  <Tooltip target="img" position="top" />
</div>
      </div>

      <DriverSummaryPanel driversSummary={driversSummary} handleDriverOrdersClick={handleDriverOrdersClick} />

      <OrdersDialog
        visible={showCancelledDialog}
        orders={cancelledOrders}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        title={dialogTitle}
        onHide={() => setShowCancelledDialog(false)}
      />

      {/* Google Map */}
      <GoogleMap
        center={mapCenter}
        zoom={12}
        onLoad={(m) => void (mapRef.current = m)}
        mapContainerStyle={{ width: "100%", height: "580px" }}
      >
        <OrdersLayer
          markers={markers}
          groups={groups}
          selectedLocKey={selectedLocKey}
          setSelectedLocKey={setSelectedLocKey}
          selectedOrderId={selectedOrderId}
          setSelectedOrderId={setSelectedOrderId}
          mapRef={mapRef}
          mapCenter={mapCenter}
          setMapCenter={setMapCenter}
          highlightLocKeys={highlightLocKeys}
          selectedDriverId={selectedDriverId}

        />
      </GoogleMap>
    </div>
  );
}
